<script setup>
import {
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  onActivated,
  onDeactivated,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import PurchaseIntegrationsAPI from 'dashboard/api/purchaseIntegrations';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import { useStoreGetters } from 'dashboard/composables/store';

const { t } = useI18n();
const getters = useStoreGetters();
const accountId = computed(() => getters.getCurrentAccountId.value);
const loading = ref(true);
const loadError = ref(false);
const providers = ref([]);
const recentEvents = ref([]);
const saving = ref(null);
const contextGeneration = ref(0);
const loadRequestId = ref(0);
const loadedContextGeneration = ref(null);
const destroyed = ref(false);
const active = ref(true);
const mounted = ref(false);
const forms = ref({
  asaas: { asaas_access_token: '', api_key: '' },
  hotmart: { hottok: '' },
});
// Provedor conectado cuja credencial está sendo trocada (sem desconectar).
const editing = ref(null);
// Provedor esperando confirmação para desabilitar.
const pendingDisconnect = ref(null);
const disableDialogRef = ref(null);

const connectedCount = computed(
  () => providers.value.filter(p => p.connected).length
);

function humanDate(value) {
  return value
    ? new Date(value).toLocaleString()
    : t('INTEGRATION_SETTINGS.PURCHASE.NOT_AVAILABLE');
}

function statusClass(provider) {
  if (provider.last_ingress_error) return 'text-red-600 dark:text-red-400';
  if (provider.connected) return 'text-green-700 dark:text-green-400';
  return 'text-n-slate-11';
}

function hasCredentialForm(provider) {
  return Object.prototype.hasOwnProperty.call(forms.value, provider.id);
}

// Na troca de credencial só vai o que foi preenchido: campo vazio apagaria a
// chave que já está salva (o backend faz merge das settings).
function filledSettings(providerId) {
  return Object.fromEntries(
    Object.entries(forms.value[providerId] || {}).filter(([, value]) =>
      String(value || '').trim()
    )
  );
}

function canSaveCredential(provider) {
  return Object.keys(filledSettings(provider.id)).length > 0;
}

// Mostra o motivo que o backend devolveu; só cai no texto genérico sem ele.
function errorMessage(error) {
  const data = error?.response?.data || {};
  return (
    data.message || data.error || t('INTEGRATION_SETTINGS.PURCHASE.SAVE_ERROR')
  );
}

function resetForms() {
  forms.value = {
    asaas: { asaas_access_token: '', api_key: '' },
    hotmart: { hottok: '' },
  };
}

function isCurrentContext(generation, currentAccountId) {
  return (
    !destroyed.value &&
    active.value &&
    generation === contextGeneration.value &&
    currentAccountId === accountId.value
  );
}

async function load() {
  const requestId = loadRequestId.value + 1;
  loadRequestId.value = requestId;
  const generation = contextGeneration.value;
  const currentAccountId = accountId.value;
  loading.value = true;
  loadError.value = false;
  try {
    const { data } = await PurchaseIntegrationsAPI.getStatus();
    if (
      !isCurrentContext(generation, currentAccountId) ||
      requestId !== loadRequestId.value
    )
      return;
    providers.value = data.providers || [];
    recentEvents.value = data.recent_events || [];
    loadedContextGeneration.value = generation;
  } catch (error) {
    if (
      !isCurrentContext(generation, currentAccountId) ||
      requestId !== loadRequestId.value
    )
      return;
    loadError.value = true;
    const status = error?.response?.status;
    if (
      [401, 403, 404].includes(status) ||
      loadedContextGeneration.value === null
    ) {
      providers.value = [];
      recentEvents.value = [];
      loadedContextGeneration.value = null;
    }
  } finally {
    if (
      isCurrentContext(generation, currentAccountId) &&
      requestId === loadRequestId.value
    ) {
      loading.value = false;
    }
  }
}

async function connect(provider) {
  const generation = contextGeneration.value;
  const currentAccountId = accountId.value;
  saving.value = provider.id;
  try {
    const payload = {
      purchase_integration: {
        settings: forms.value[provider.id] || {},
      },
    };
    await PurchaseIntegrationsAPI.connect(provider.id, payload);
    if (!isCurrentContext(generation, currentAccountId)) return;
    useAlert(t('INTEGRATION_SETTINGS.PURCHASE.CONNECTED'));
    await load();
  } catch (error) {
    if (isCurrentContext(generation, currentAccountId)) {
      useAlert(errorMessage(error));
    }
  } finally {
    if (isCurrentContext(generation, currentAccountId)) saving.value = null;
  }
}

async function disconnect(provider) {
  if (provider.id === 'shopify') return;
  const generation = contextGeneration.value;
  const currentAccountId = accountId.value;
  saving.value = provider.id;
  try {
    await PurchaseIntegrationsAPI.disconnect(provider.id);
    if (!isCurrentContext(generation, currentAccountId)) return;
    useAlert(t('INTEGRATION_SETTINGS.PURCHASE.DISCONNECTED'));
    await load();
  } catch (error) {
    if (isCurrentContext(generation, currentAccountId)) {
      useAlert(errorMessage(error));
    }
  } finally {
    if (isCurrentContext(generation, currentAccountId)) saving.value = null;
  }
}

function askDisconnect(provider) {
  pendingDisconnect.value = provider;
  disableDialogRef.value?.open();
}

async function confirmDisconnect() {
  const provider = pendingDisconnect.value;
  disableDialogRef.value?.close();
  pendingDisconnect.value = null;
  if (provider) await disconnect(provider);
}

function startEditing(provider) {
  resetForms();
  editing.value = provider.id;
}

function cancelEditing() {
  editing.value = null;
  resetForms();
}

async function updateCredential(provider) {
  if (!canSaveCredential(provider)) return;
  const generation = contextGeneration.value;
  const currentAccountId = accountId.value;
  saving.value = provider.id;
  try {
    await PurchaseIntegrationsAPI.update(provider.id, {
      purchase_integration: { settings: filledSettings(provider.id) },
    });
    if (!isCurrentContext(generation, currentAccountId)) return;
    useAlert(t('INTEGRATION_SETTINGS.PURCHASE.CREDENTIAL_UPDATED'));
    cancelEditing();
    await load();
  } catch (error) {
    if (isCurrentContext(generation, currentAccountId)) {
      useAlert(errorMessage(error));
    }
  } finally {
    if (isCurrentContext(generation, currentAccountId)) saving.value = null;
  }
}

async function copyUrl(url) {
  if (!url) return;
  try {
    await navigator.clipboard.writeText(url);
    useAlert(t('INTEGRATION_SETTINGS.PURCHASE.COPY_SUCCESS'));
  } catch {
    useAlert(t('INTEGRATION_SETTINGS.PURCHASE.COPY_ERROR'));
  }
}

watch(accountId, () => {
  contextGeneration.value += 1;
  loadedContextGeneration.value = null;
  loading.value = true;
  loadError.value = false;
  providers.value = [];
  recentEvents.value = [];
  saving.value = null;
  editing.value = null;
  resetForms();
  if (active.value) load();
});

onMounted(() => {
  mounted.value = true;
  load();
});
onActivated(() => {
  if (!mounted.value || active.value) return;
  active.value = true;
  load();
});
onDeactivated(() => {
  active.value = false;
  contextGeneration.value += 1;
  loadRequestId.value += 1;
  loadedContextGeneration.value = null;
  loading.value = false;
  loadError.value = false;
  providers.value = [];
  recentEvents.value = [];
  saving.value = null;
  editing.value = null;
  resetForms();
});
onBeforeUnmount(() => {
  destroyed.value = true;
  active.value = false;
  contextGeneration.value += 1;
  loadRequestId.value += 1;
});
</script>

<template>
  <SettingsLayout
    :is-loading="loading"
    :loading-message="$t('INTEGRATION_SETTINGS.PURCHASE.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('INTEGRATION_SETTINGS.PURCHASE.HEADER')"
        :description="$t('INTEGRATION_SETTINGS.PURCHASE.DESCRIPTION')"
        feature-name="integrations"
      />
    </template>
    <template #body>
      <div
        class="flex flex-col gap-6 max-w-5xl"
        data-testid="page-purchase-integrations"
      >
        <div
          v-if="loadError"
          class="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-n-ruby-8 bg-n-ruby-1 p-4"
          role="alert"
        >
          <p class="text-sm text-n-ruby-11">
            {{ $t('INTEGRATION_SETTINGS.PURCHASE.LOAD_ERROR') }}
          </p>
          <Button
            size="sm"
            variant="faded"
            :label="$t('INTEGRATION_SETTINGS.PURCHASE.RETRY')"
            @click="load"
          />
        </div>
        <p class="text-sm text-n-slate-11">
          {{
            $t('INTEGRATION_SETTINGS.PURCHASE.SUMMARY', {
              count: connectedCount,
            })
          }}
        </p>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div
            v-for="provider in providers"
            :key="provider.id"
            class="rounded-2xl border border-n-weak bg-n-solid-1 p-4 flex flex-col gap-3"
          >
            <div class="flex items-start justify-between gap-2">
              <div>
                <h3 class="text-base font-semibold text-n-slate-12">
                  {{ provider.label }}
                </h3>
                <p class="text-xs font-medium" :class="statusClass(provider)">
                  {{
                    provider.connected
                      ? $t('INTEGRATION_SETTINGS.PURCHASE.STATUS_CONNECTED')
                      : $t('INTEGRATION_SETTINGS.PURCHASE.STATUS_OFF')
                  }}
                </p>
              </div>
              <span
                v-if="provider.last_ingress_status"
                class="text-xs font-mono text-n-slate-11"
              >
                {{ provider.last_ingress_status }}
              </span>
            </div>

            <div
              v-if="provider.id === 'shopify'"
              class="text-sm text-n-slate-11"
            >
              {{
                provider.shop_domain
                  ? $t('INTEGRATION_SETTINGS.PURCHASE.SHOPIFY_STORE', {
                      shop: provider.shop_domain,
                    })
                  : $t('INTEGRATION_SETTINGS.PURCHASE.SHOPIFY_HINT')
              }}
            </div>

            <div v-else-if="provider.webhook_url" class="flex flex-col gap-1">
              <span class="text-xs text-n-slate-11">{{
                $t('INTEGRATION_SETTINGS.PURCHASE.WEBHOOK_URL')
              }}</span>
              <code class="text-xs break-all bg-n-slate-3 rounded px-2 py-1">{{
                provider.webhook_url
              }}</code>
              <Button
                size="sm"
                variant="faded"
                class="self-start"
                @click="copyUrl(provider.webhook_url)"
              >
                {{ $t('INTEGRATION_SETTINGS.PURCHASE.COPY_URL') }}
              </Button>
            </div>

            <div
              v-if="
                provider.id === 'asaas' &&
                (!provider.connected || editing === provider.id)
              "
              class="flex flex-col gap-2"
            >
              <Input
                v-model="forms.asaas.asaas_access_token"
                :label="$t('INTEGRATION_SETTINGS.PURCHASE.ASAAS_TOKEN')"
                type="password"
              />
              <Input
                v-model="forms.asaas.api_key"
                :label="$t('INTEGRATION_SETTINGS.PURCHASE.ASAAS_API_KEY')"
                type="password"
              />
            </div>

            <div
              v-if="
                provider.id === 'hotmart' &&
                (!provider.connected || editing === provider.id)
              "
              class="flex flex-col gap-2"
            >
              <Input
                v-model="forms.hotmart.hottok"
                :label="$t('INTEGRATION_SETTINGS.PURCHASE.HOTMART_HOTTOK')"
                type="password"
              />
            </div>

            <p
              v-if="provider.last_ingress_error"
              class="text-xs text-red-600 dark:text-red-400"
            >
              {{ provider.last_ingress_error }}
            </p>

            <div class="flex gap-2 mt-auto">
              <Button
                v-if="provider.id !== 'shopify' && !provider.connected"
                size="sm"
                :is-loading="saving === provider.id"
                @click="connect(provider)"
              >
                {{ $t('INTEGRATION_SETTINGS.PURCHASE.ENABLE') }}
              </Button>
              <template v-if="editing === provider.id && provider.connected">
                <Button
                  size="sm"
                  :disabled="!canSaveCredential(provider)"
                  :is-loading="saving === provider.id"
                  @click="updateCredential(provider)"
                >
                  {{ $t('INTEGRATION_SETTINGS.PURCHASE.SAVE_CREDENTIAL') }}
                </Button>
                <Button
                  size="sm"
                  variant="faded"
                  color="slate"
                  @click="cancelEditing"
                >
                  {{ $t('INTEGRATION_SETTINGS.PURCHASE.CANCEL') }}
                </Button>
              </template>
              <template
                v-else-if="provider.id !== 'shopify' && provider.connected"
              >
                <Button
                  v-if="hasCredentialForm(provider)"
                  size="sm"
                  variant="faded"
                  color="slate"
                  @click="startEditing(provider)"
                >
                  {{ $t('INTEGRATION_SETTINGS.PURCHASE.UPDATE_CREDENTIAL') }}
                </Button>
                <Button
                  size="sm"
                  variant="faded"
                  color="ruby"
                  :is-loading="saving === provider.id"
                  @click="askDisconnect(provider)"
                >
                  {{ $t('INTEGRATION_SETTINGS.PURCHASE.DISABLE') }}
                </Button>
              </template>
            </div>
          </div>
        </div>

        <section class="rounded-2xl border border-n-weak bg-n-solid-1 p-4">
          <h3 class="text-base font-semibold text-n-slate-12 mb-3">
            {{ $t('INTEGRATION_SETTINGS.PURCHASE.RECENT_EVENTS') }}
          </h3>
          <div
            v-if="recentEvents.length === 0 && !loadError"
            class="text-sm text-n-slate-11"
          >
            {{ $t('INTEGRATION_SETTINGS.PURCHASE.NO_EVENTS') }}
          </div>
          <div v-else class="overflow-x-auto">
            <table class="w-full text-sm text-left">
              <thead class="text-xs text-n-slate-11 border-b border-n-weak">
                <tr>
                  <th class="py-2 pr-3">
                    {{ $t('INTEGRATION_SETTINGS.PURCHASE.COL_TIME') }}
                  </th>
                  <th class="py-2 pr-3">
                    {{ $t('INTEGRATION_SETTINGS.PURCHASE.COL_SOURCE') }}
                  </th>
                  <th class="py-2 pr-3">
                    {{ $t('INTEGRATION_SETTINGS.PURCHASE.COL_TX') }}
                  </th>
                  <th class="py-2 pr-3">
                    {{ $t('INTEGRATION_SETTINGS.PURCHASE.COL_LAUNCH') }}
                  </th>
                  <th class="py-2 pr-3">
                    {{ $t('INTEGRATION_SETTINGS.PURCHASE.COL_STATUS') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="event in recentEvents"
                  :key="event.id"
                  class="border-b border-n-weak last:border-0"
                >
                  <td class="py-2 pr-3 whitespace-nowrap">
                    {{ humanDate(event.received_at) }}
                  </td>
                  <td class="py-2 pr-3">{{ event.source }}</td>
                  <td class="py-2 pr-3 font-mono text-xs">
                    {{ event.transaction_id }}
                  </td>
                  <td class="py-2 pr-3">{{ event.launch_id || '—' }}</td>
                  <td class="py-2 pr-3">
                    <span v-if="event.current_error" class="text-red-600">{{
                      event.current_error
                    }}</span>
                    <span v-else>{{ event.status }}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      </div>
      <Dialog
        ref="disableDialogRef"
        type="alert"
        :title="
          $t('INTEGRATION_SETTINGS.PURCHASE.DISABLE_CONFIRM_TITLE', {
            provider: pendingDisconnect?.label || '',
          })
        "
        :description="
          $t('INTEGRATION_SETTINGS.PURCHASE.DISABLE_CONFIRM_DESCRIPTION', {
            provider: pendingDisconnect?.label || '',
          })
        "
        :confirm-button-label="$t('INTEGRATION_SETTINGS.PURCHASE.DISABLE')"
        :cancel-button-label="$t('INTEGRATION_SETTINGS.PURCHASE.CANCEL')"
        @confirm="confirmDisconnect"
        @close="pendingDisconnect = null"
      />
    </template>
  </SettingsLayout>
</template>
